import { 
  collection, 
  doc, 
  getDoc,
  getDocs,
  setDoc, 
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  writeBatch,
  Timestamp,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase-config';
import VacationModel, { 
  VacationDateRange, 
  VacationSummary, 
  VacationValidation,
  VacationHelpers 
} from '../models/VacationModels';
// ✅ NEW: Import HolidayService for public holiday checking
import HolidayService from './HolidayService';

/**
 * VacationService - Handles all vacation-related Firestore operations
 * Collection Path: companies/{companyId}/vacations/{vacationId}
 * 
 * IMPORTANT: 
 * - All dates are stored as strings in "YYYY-MM-DD" format to avoid timezone conversion issues
 * - Duration is stored in MINUTES for precision and consistency with shifts
 * - ALL methods return proper VacationModel instances (not plain objects) to preserve methods
 * - ✅ NEW: Only creates vacation docs for days with shifts AND not public holidays (matches iOS)
 * 
 * AUTO-RECALCULATION: Vacation duration is automatically recalculated
 * based on the TOTAL minutes of ALL shifts on that date/department
 * 
 * CASCADE SYNC: Three internal helpers keep vacations in sync with shift changes.
 * These are called by ScheduleService after confirmed shift operations and never
 * throw errors to the UI:
 * - deleteVacationForDateIfExists: Removes vacation when no shifts remain on a date
 * - updateVacationDurationForDate: Updates vacation duration when a shift is added or changed
 * - reconcileVacationsWithShifts: Full reconciliation after batch/week schedule saves
 */
class VacationService {
  /**
   * Convert Date object to YYYY-MM-DD string format
   * ✅ ENHANCED: Better validation and error messages
   * @private
   */
  _toDateString(date) {
    // Already a valid YYYY-MM-DD string
    if (typeof date === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Try to parse if it's a different format
      console.warn('⚠️ Non-standard date string format:', date);
      date = new Date(date);
    }
    
    // Validate Date object
    if (!(date instanceof Date)) {
      console.error('❌ Not a Date object:', date, 'Type:', typeof date);
      throw new Error(`Invalid date provided: expected Date object, got ${typeof date}`);
    }
    
    if (isNaN(date.getTime())) {
      console.error('❌ Invalid Date object (NaN):', date);
      throw new Error('Invalid date provided: Date object is invalid (NaN)');
    }
    
    // Convert to YYYY-MM-DD
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Add a single vacation day
   * @returns {VacationModel} - VacationModel instance with all methods
   */
  async addVacation(companyId, vacationData) {
    try {
      console.log('➕ Adding vacation:', { companyId, vacationData });
      
      // Validate data
      const validation = VacationValidation.validate(vacationData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Ensure date is in string format
      const dateString = this._toDateString(vacationData.date);

      // Check for duplicate
      const duplicate = await this.checkDuplicate(
        companyId,
        vacationData.staffId,
        dateString,
        vacationData.departmentId
      );

      if (duplicate) {
        throw new Error('Für diesen Tag existiert bereits ein Urlaubseintrag');
      }

      // Calculate duration from ALL shifts on this date (in minutes)
      const durationMinutes = await this.calculateTotalMinutesFromShifts(
        companyId,
        vacationData.staffId,
        dateString,
        vacationData.departmentId
      );

      console.log(`📊 Calculated ${durationMinutes} minutes (${VacationHelpers.formatDurationFromMinutes(durationMinutes)}) for vacation on ${dateString}`);

      // Create vacation model
      const vacation = new VacationModel({
        ...vacationData,
        date: dateString, // Store as string
        durationMinutes,
        createdAt: new Date()
      });

      // Generate custom ID
      const vacationId = VacationHelpers.generateVacationId(
        vacation.staffId,
        dateString,
        vacation.departmentId
      );

      // Save to Firestore
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      await setDoc(vacationRef, vacation.toFirestore());

      console.log(`✅ Vacation added: ${vacationId}`);

      // ✅ Return VacationModel instance with id
      return new VacationModel({ id: vacationId, ...vacation });
    } catch (error) {
      console.error('❌ Error adding vacation:', error);
      throw error;
    }
  }

  /**
   * Add multiple vacation days (date range)
   * ✅ UPDATED: Only creates vacation docs for days with shifts AND not public holidays
   * This matches iOS behavior exactly
   * 
   * @returns {Object} - Object with count, totalMinutes, and array of VacationModel instances
   */
  async addVacationRange(companyId, staffId, departmentId, startDate, endDate, createdBy) {
    try {
      console.log('➕ Adding vacation range:', { companyId, staffId, departmentId, startDate, endDate });
      
      // Validate date range
      const validation = VacationValidation.validateDateRange(startDate, endDate);
      if (!validation.isValid) {
        const errors = validation.errors.filter(e => typeof e === 'string');
        throw new Error(errors.join(', '));
      }

      // Get all dates in range
      const dateRange = new VacationDateRange(startDate, endDate);
      const dates = dateRange.getDates();

      // Check for duplicates
      const duplicates = [];
      for (const date of dates) {
        const dateString = this._toDateString(date);
        const exists = await this.checkDuplicate(companyId, staffId, dateString, departmentId);
        if (exists) {
          duplicates.push(dateString);
        }
      }

      if (duplicates.length > 0) {
        throw new Error(`Urlaubseinträge existieren bereits für: ${duplicates.join(', ')}`);
      }

      // ✅ NEW: Fetch public holidays for the date range
      const startYear = new Date(startDate).getFullYear();
      const endYear = new Date(endDate).getFullYear();
      
      let holidays = [];
      try {
        if (startYear === endYear) {
          // Single year - fetch once
          holidays = await HolidayService.fetchHolidaysForYear(companyId, startYear);
        } else {
          // Range spans multiple years - fetch both
          const [holidays1, holidays2] = await Promise.all([
            HolidayService.fetchHolidaysForYear(companyId, startYear),
            HolidayService.fetchHolidaysForYear(companyId, endYear)
          ]);
          holidays = [...holidays1, ...holidays2];
        }
        console.log(`📅 Fetched ${holidays.length} public holiday(s) for date range`);
      } catch (err) {
        console.warn('⚠️ Could not fetch holidays, continuing without holiday filtering:', err);
        // Continue without holiday filtering if fetch fails (fallback)
      }
      
      // ✅ NEW: Create a Set of holiday date strings for O(1) lookup
      const holidayDateStrings = new Set(holidays.map(h => h.dateString));

      // Calculate minutes for each date from ALL shifts and check if it's a holiday
      const vacationsWithMinutes = await Promise.all(
        dates.map(async (date) => {
          const dateString = this._toDateString(date);
          const durationMinutes = await this.calculateTotalMinutesFromShifts(
            companyId,
            staffId,
            dateString,
            departmentId
          );
          const isPublicHoliday = holidayDateStrings.has(dateString);
          return { date: dateString, durationMinutes, isPublicHoliday };
        })
      );

      // ✅ NEW: Filter to only include days WITH shifts AND NOT public holidays
      const validVacationDays = vacationsWithMinutes.filter(v => {
        const hasShift = v.durationMinutes > 0;
        const notHoliday = !v.isPublicHoliday;
        
        // Log exclusions for transparency
        if (!hasShift) {
          console.log(`  ⏭️ Skipping ${v.date}: No shift scheduled (${v.durationMinutes} min)`);
        }
        if (v.isPublicHoliday) {
          console.log(`  ⏭️ Skipping ${v.date}: Public holiday`);
        }
        
        return hasShift && notHoliday;
      });

      // ✅ NEW: Check if any valid days remain after filtering
      if (validVacationDays.length === 0) {
        // Provide specific error messages based on why filtering failed
        const allNoShifts = vacationsWithMinutes.every(v => v.durationMinutes === 0);
        const allHolidays = vacationsWithMinutes.every(v => v.isPublicHoliday);
        const mixedReasons = !allNoShifts && !allHolidays;
        
        if (allNoShifts) {
          throw new Error('Keine geplanten Schichten in diesem Zeitraum gefunden');
        } else if (allHolidays) {
          throw new Error('Alle ausgewählten Tage sind Feiertage');
        } else if (mixedReasons) {
          throw new Error('Keine gültigen Urlaubstage gefunden (nur Feiertage oder Tage ohne Schichten)');
        } else {
          throw new Error('Keine gültigen Urlaubstage im ausgewählten Zeitraum');
        }
      }

      // ✅ NEW: Log filtering summary
      const filteredCount = vacationsWithMinutes.length - validVacationDays.length;
      if (filteredCount > 0) {
        const holidayCount = vacationsWithMinutes.filter(v => v.isPublicHoliday).length;
        const noShiftCount = vacationsWithMinutes.filter(v => v.durationMinutes === 0 && !v.isPublicHoliday).length;
        console.log(`ℹ️ Filtered out ${filteredCount} day(s): ${holidayCount} public holiday(s), ${noShiftCount} day(s) without shifts`);
        console.log(`✅ Creating vacation entries for ${validVacationDays.length} valid day(s)`);
      }

      // Use batch write for atomicity (only for VALID days)
      const batch = writeBatch(db);
      const createdVacations = [];

      validVacationDays.forEach(({ date, durationMinutes }) => {
        const vacation = new VacationModel({
          staffId,
          departmentId,
          date, // Already string format
          durationMinutes,
          createdBy,
          createdAt: new Date()
        });

        const vacationId = VacationHelpers.generateVacationId(
          staffId,
          date,
          departmentId
        );

        const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
        batch.set(vacationRef, vacation.toFirestore());

        // ✅ Store VacationModel instance with id
        createdVacations.push(new VacationModel({ id: vacationId, ...vacation }));
      });

      await batch.commit();

      const totalMinutes = createdVacations.reduce((sum, v) => sum + v.durationMinutes, 0);
      console.log(`✅ Added ${createdVacations.length} vacation days (${VacationHelpers.formatDurationFromMinutes(totalMinutes)} total)`);

      return {
        success: true,
        count: createdVacations.length,
        vacations: createdVacations, // ✅ Array of VacationModel instances
        totalMinutes,
        totalHours: totalMinutes / 60 // For display compatibility
      };
    } catch (error) {
      console.error('❌ Error adding vacation range:', error);
      throw error;
    }
  }

  /**
   * Update an existing vacation
   */
  async updateVacation(companyId, vacationId, updates, updatedBy) {
    try {
      console.log('✏️ Updating vacation:', { companyId, vacationId, updates });
      
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      
      // If date changed, recalculate duration from shifts
      if (updates.date) {
        const dateString = this._toDateString(updates.date);
        const vacation = await this.getVacationById(companyId, vacationId);
        updates.durationMinutes = await this.calculateTotalMinutesFromShifts(
          companyId,
          vacation.staffId,
          dateString,
          vacation.departmentId
        );
        updates.date = dateString; // Ensure string format
        console.log(`📊 Recalculated duration: ${updates.durationMinutes} min (${VacationHelpers.formatDurationFromMinutes(updates.durationMinutes)})`);
      }

      await updateDoc(vacationRef, {
        ...updates,
        updatedAt: Timestamp.fromDate(new Date()),
        updatedBy
      });

      console.log(`✅ Vacation updated: ${vacationId}`);

      return { success: true };
    } catch (error) {
      console.error('❌ Error updating vacation:', error);
      throw error;
    }
  }

  /**
   * Delete a vacation
   */
  async deleteVacation(companyId, vacationId) {
    try {
      console.log('🗑️ Deleting vacation:', { companyId, vacationId });
      
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      await deleteDoc(vacationRef);
      
      console.log(`✅ Vacation deleted: ${vacationId}`);
      
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting vacation:', error);
      throw error;
    }
  }

  /**
   * Get vacation by ID
   * @returns {VacationModel|null} - VacationModel instance or null if not found
   */
  async getVacationById(companyId, vacationId) {
    try {
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      const vacationDoc = await getDoc(vacationRef);
      
      if (!vacationDoc.exists()) {
        return null;
      }

      // ✅ Returns VacationModel instance
      return VacationModel.fromFirestore(vacationDoc);
    } catch (error) {
      console.error('Error getting vacation:', error);
      throw error;
    }
  }

  /**
   * Get all vacations for a department in a date range
   * @returns {Array<VacationModel>} - Array of VacationModel instances
   */
  async getVacationsByDepartment(companyId, departmentId, startDate, endDate) {
    try {
      console.log('🔍 getVacationsByDepartment called with:', { 
        companyId, 
        departmentId,
        startDate: startDate?.toString(), 
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('✅ Converted dates:', { startDateString, endDateString });

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('departmentId', '==', departmentId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      // ✅ Returns array of VacationModel instances
      const vacations = snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
      
      console.log(`✅ Found ${vacations.length} vacation(s) for department ${departmentId}`);
      return vacations;
    } catch (error) {
      console.error('❌ Error getting vacations by department:', error);
      throw error;
    }
  }

  /**
   * Get all vacations for a staff member in a date range
   * @returns {Array<VacationModel>} - Array of VacationModel instances
   */
  async getVacationsByStaff(companyId, staffId, startDate, endDate) {
    try {
      console.log('🔍 getVacationsByStaff called with:', { 
        companyId, 
        staffId, 
        startDate: startDate?.toString(), 
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('✅ Converted dates:', { startDateString, endDateString });

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('staffId', '==', staffId),
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      // ✅ Returns array of VacationModel instances
      const vacations = snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
      
      console.log(`✅ Found ${vacations.length} vacation(s) for staff ${staffId}`);
      return vacations;
    } catch (error) {
      console.error('❌ Error getting vacations by staff:', error);
      throw error;
    }
  }

  /**
   * Get all vacations for a company in a date range
   * @returns {Array<VacationModel>} - Array of VacationModel instances
   */
  async getVacationsByDateRange(companyId, startDate, endDate) {
    try {
      console.log('🔍 getVacationsByDateRange called with:', { 
        companyId,
        startDate: startDate?.toString(), 
        endDate: endDate?.toString(),
        startDateType: typeof startDate,
        endDateType: typeof endDate
      });

      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      console.log('✅ Converted dates:', { startDateString, endDateString });

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('date', '>=', startDateString),
        where('date', '<=', endDateString),
        orderBy('date', 'asc')
      );

      const snapshot = await getDocs(q);
      // ✅ Returns array of VacationModel instances
      const vacations = snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
      
      console.log(`✅ Found ${vacations.length} vacation(s) in date range`);
      return vacations;
    } catch (error) {
      console.error('❌ Error getting vacations by date range:', error);
      throw error;
    }
  }

  /**
   * Get all vacations for a specific day
   * @returns {Array<VacationModel>} - Array of VacationModel instances
   */
  async getVacationsForDay(companyId, date, departmentIds = null) {
    try {
      const dateString = this._toDateString(date);

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      
      let q;
      if (departmentIds && departmentIds.length > 0) {
        // Filter by specific departments
        q = query(
          vacationsRef,
          where('departmentId', 'in', departmentIds),
          where('date', '==', dateString)
        );
      } else {
        // All departments
        q = query(
          vacationsRef,
          where('date', '==', dateString)
        );
      }

      const snapshot = await getDocs(q);
      // ✅ Returns array of VacationModel instances
      return snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
    } catch (error) {
      console.error('Error getting vacations for day:', error);
      throw error;
    }
  }

  /**
   * Get current week vacations for dashboard widget
   * @returns {Array<VacationModel>} - Array of VacationModel instances
   */
  async getCurrentWeekVacations(companyId) {
    try {
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      startOfWeek.setHours(0, 0, 0, 0);

      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
      endOfWeek.setHours(23, 59, 59, 999);

      return await this.getVacationsByDateRange(companyId, startOfWeek, endOfWeek);
    } catch (error) {
      console.error('Error getting current week vacations:', error);
      throw error;
    }
  }

  /**
   * Check if vacation already exists for staff on date
   */
  async checkDuplicate(companyId, staffId, date, departmentId) {
    try {
      const dateString = this._toDateString(date);
      const vacationId = VacationHelpers.generateVacationId(staffId, dateString, departmentId);
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      const vacationDoc = await getDoc(vacationRef);
      return vacationDoc.exists();
    } catch (error) {
      console.error('Error checking duplicate:', error);
      return false;
    }
  }

  /**
   * Calculate TOTAL minutes from ALL shifts for a specific date
   * 
   * ✅ UPDATED: Returns minutes instead of hours for precision
   * 
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string|Date} date - Date in YYYY-MM-DD format or Date object
   * @param {string} departmentId - Department ID
   * @returns {number} Total minutes from all shifts (0 if no shifts)
   */
  async calculateTotalMinutesFromShifts(companyId, staffId, date, departmentId) {
    try {
      const dateStr = this._toDateString(date);
      
      console.log('📊 Calculating minutes from shifts:', { 
        companyId, 
        staffId, 
        dateStr, 
        departmentId 
      });

      // Query ALL shifts for this staff member on this date
      const shiftsRef = collection(db, 'companies', companyId, 'schedules', staffId, 'shifts');
      const q = query(
        shiftsRef,
        where('date', '==', dateStr),
        where('departmentId', '==', departmentId)
      );

      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        console.log(`ℹ️ No shifts found for ${staffId} on ${dateStr} in dept ${departmentId}`);
        return 0; // No shifts scheduled
      }

      console.log(`📋 Found ${snapshot.size} shift(s) to process`);

      // ✅ SUM ALL SHIFTS in MINUTES
      let totalMinutes = 0;
      
      snapshot.docs.forEach((doc, index) => {
        const shiftData = doc.data();
        let minutes = 0;
        
        console.log(`  Shift ${index + 1}:`, {
          id: doc.id,
          totalMinutes: shiftData.totalMinutes,
          durationMinutes: shiftData.durationMinutes,
          expectedHours: shiftData.expectedHours,
          startTime: shiftData.startTime,
          endTime: shiftData.endTime
        });
        
        // Priority 1: Use totalMinutes if available (most common)
        if (shiftData.totalMinutes !== undefined && shiftData.totalMinutes !== null) {
          minutes = Number(shiftData.totalMinutes) || 0;
          console.log(`    → Using totalMinutes: ${minutes} min`);
        }
        // Priority 2: Use durationMinutes (alternative field name)
        else if (shiftData.durationMinutes !== undefined && shiftData.durationMinutes !== null) {
          minutes = Number(shiftData.durationMinutes) || 0;
          console.log(`    → Using durationMinutes: ${minutes} min`);
        }
        // Priority 3: Calculate from expectedHours
        else if (shiftData.expectedHours !== undefined && shiftData.expectedHours !== null) {
          minutes = Math.round(Number(shiftData.expectedHours) * 60) || 0;
          console.log(`    → Using expectedHours: ${shiftData.expectedHours}h = ${minutes} min`);
        }
        // Priority 4: Calculate from start/end Timestamps
        else if (shiftData.startTime && shiftData.endTime) {
          try {
            const start = shiftData.startTime.toDate ? shiftData.startTime.toDate() : new Date(shiftData.startTime);
            const end = shiftData.endTime.toDate ? shiftData.endTime.toDate() : new Date(shiftData.endTime);
            const durationMs = end - start;
            minutes = Math.round(durationMs / (1000 * 60));
            console.log(`    → Calculated from timestamps: ${minutes} min`);
          } catch (err) {
            console.warn(`    ⚠️ Failed to parse timestamps:`, err);
          }
        } else {
          console.warn(`    ⚠️ Could not determine minutes from shift data`);
        }
        
        totalMinutes += minutes;
      });

      console.log(`✅ Total minutes calculated: ${totalMinutes} min (${VacationHelpers.formatDurationFromMinutes(totalMinutes)}) from ${snapshot.size} shift(s)`);
      return totalMinutes;
      
    } catch (error) {
      console.error('❌ Error calculating total minutes from shifts:', error);
      return 0; // Default to 0 on error
    }
  }

  /**
   * Recalculate vacation duration when shift is created/updated/deleted
   * 
   * ✅ UPDATED: Now uses minutes instead of hours
   * 
   * This is triggered automatically by ScheduleService after any shift change
   * 
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string|Date} date - Date in YYYY-MM-DD format or Date object
   * @param {string} departmentId - Department ID
   */
  async recalculateVacationMinutes(companyId, staffId, date, departmentId) {
    try {
      const dateString = this._toDateString(date);
      
      console.log('🔄 Recalculating vacation minutes:', { 
        companyId, 
        staffId, 
        dateString, 
        departmentId 
      });
      
      // Check if vacation exists for this date
      const vacationId = VacationHelpers.generateVacationId(staffId, dateString, departmentId);
      const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
      const vacationDoc = await getDoc(vacationRef);

      if (!vacationDoc.exists()) {
        // No vacation on this date - nothing to recalculate
        console.log(`ℹ️ No vacation found for ${dateString} - skipping recalculation`);
        return;
      }

      console.log(`📋 Vacation exists (${vacationId}), recalculating minutes...`);

      // ✅ ALWAYS query shifts for accurate total minutes
      const totalMinutes = await this.calculateTotalMinutesFromShifts(
        companyId,
        staffId,
        dateString,
        departmentId
      );

      // Get current minutes for comparison
      const currentData = vacationDoc.data();
      const currentMinutes = currentData.durationMinutes || 0;

      if (currentMinutes === totalMinutes) {
        console.log(`ℹ️ Minutes unchanged (${totalMinutes} min), skipping update`);
        return;
      }

      // Update vacation with recalculated minutes
      await updateDoc(vacationRef, {
        durationMinutes: totalMinutes,
        updatedAt: Timestamp.fromDate(new Date()),
        updatedBy: 'system',
        lastRecalculatedAt: Timestamp.fromDate(new Date())
      });

      console.log(`✅ Vacation minutes updated: ${vacationId} (${currentMinutes} min → ${totalMinutes} min / ${VacationHelpers.formatDurationFromMinutes(totalMinutes)})`);
      
    } catch (error) {
      console.error('❌ Error recalculating vacation minutes:', error);
      // Don't throw - this is a background operation
    }
  }

  /**
   * Reset vacation minutes when shift is deleted
   * 
   * ✅ UPDATED: Now recalculates from remaining shifts
   * 
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {string|Date} date - Date in YYYY-MM-DD format or Date object
   * @param {string} departmentId - Department ID
   */
  async resetVacationMinutes(companyId, staffId, date, departmentId) {
    console.log('🔄 Resetting vacation minutes (recalculating from remaining shifts)');
    // ✅ Use same recalculation logic - will naturally return 0 if no shifts remain
    await this.recalculateVacationMinutes(companyId, staffId, date, departmentId);
  }

  /**
   * Batch recalculate vacation minutes for multiple dates
   * Used when bulk operations on shifts occur
   * 
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff ID
   * @param {Array<string|Date>} dates - Array of dates (YYYY-MM-DD strings or Date objects)
   * @param {string} departmentId - Department ID
   */
  async batchRecalculateVacationMinutes(companyId, staffId, dates, departmentId) {
    try {
      console.log(`🔄 Batch recalculating ${dates.length} vacation dates`);
      
      const batch = writeBatch(db);
      let updateCount = 0;
      
      for (const date of dates) {
        const dateString = this._toDateString(date);
        
        // Check if vacation exists
        const vacationId = VacationHelpers.generateVacationId(staffId, dateString, departmentId);
        const vacationRef = doc(db, 'companies', companyId, 'vacations', vacationId);
        const vacationDoc = await getDoc(vacationRef);

        if (!vacationDoc.exists()) {
          console.log(`  ℹ️ No vacation found for ${dateString} - skipping`);
          continue; // No vacation to recalculate
        }

        // ✅ Calculate total minutes from ALL shifts
        const totalMinutes = await this.calculateTotalMinutesFromShifts(
          companyId,
          staffId,
          dateString,
          departmentId
        );

        batch.update(vacationRef, {
          durationMinutes: totalMinutes,
          updatedAt: Timestamp.fromDate(new Date()),
          updatedBy: 'system',
          lastRecalculatedAt: Timestamp.fromDate(new Date())
        });
        updateCount++;
        
        console.log(`  ✅ Queued update: ${dateString} → ${totalMinutes} min (${VacationHelpers.formatDurationFromMinutes(totalMinutes)})`);
      }

      if (updateCount > 0) {
        await batch.commit();
        console.log(`✅ Batch recalculated ${updateCount} vacation minute(s)`);
      } else {
        console.log(`ℹ️ No vacations to recalculate`);
      }
    } catch (error) {
      console.error('❌ Error batch recalculating vacation minutes:', error);
    }
  }

  // ==================== CASCADE SYNC HELPERS ====================
  // These methods are called by ScheduleService after confirmed shift operations.
  // They are internal helpers and never throw errors to the UI.
  // They query vacations by staffId + date (not by deterministic ID) because
  // departmentId is not available in the shift cascade context.

  /**
   * Delete vacation document(s) for a specific staff member on a specific date.
   *
   * Called when a shift is deleted and no other shifts remain on that date.
   * If no vacation documents are found, does nothing.
   * Uses a batch delete to handle the rare case of multiple vacation docs per date.
   *
   * IMPORTANT: Never throws to the UI. Fails silently with console.error only.
   *
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff member ID
   * @param {string} dateString - Date in YYYY-MM-DD format
   */
  async deleteVacationForDateIfExists(companyId, staffId, dateString) {
    try {
      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('staffId', '==', staffId),
        where('date', '==', dateString)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(`Cascade: no vacation found for staff ${staffId} on ${dateString} - nothing to delete`);
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach(vacationDoc => {
        batch.delete(vacationDoc.ref);
      });
      await batch.commit();

      console.log(`Cascade: deleted ${snapshot.size} vacation(s) for staff ${staffId} on ${dateString}`);
    } catch (error) {
      console.error(`Cascade error - deleteVacationForDateIfExists (staffId: ${staffId}, date: ${dateString}):`, error);
    }
  }

  /**
   * Update the durationMinutes of existing vacation document(s) for a specific date.
   *
   * Called when a shift is created or updated on a date that already has a vacation.
   * If no vacation documents are found, does nothing.
   * Never creates new vacation documents.
   *
   * IMPORTANT: Never throws to the UI. Fails silently with console.error only.
   *
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff member ID
   * @param {string} dateString - Date in YYYY-MM-DD format
   * @param {number} newDurationMinutes - New duration in minutes to set on the vacation
   */
  async updateVacationDurationForDate(companyId, staffId, dateString, newDurationMinutes) {
    try {
      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('staffId', '==', staffId),
        where('date', '==', dateString)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(`Cascade: no vacation found for staff ${staffId} on ${dateString} - skipping duration update`);
        return;
      }

      const batch = writeBatch(db);
      snapshot.docs.forEach(vacationDoc => {
        batch.update(vacationDoc.ref, {
          durationMinutes: newDurationMinutes,
          updatedAt: Timestamp.fromDate(new Date()),
          updatedBy: 'system'
        });
      });
      await batch.commit();

      console.log(`Cascade: updated vacation duration for staff ${staffId} on ${dateString} to ${newDurationMinutes} min`);
    } catch (error) {
      console.error(`Cascade error - updateVacationDurationForDate (staffId: ${staffId}, date: ${dateString}, minutes: ${newDurationMinutes}):`, error);
    }
  }

  /**
   * Reconcile all vacation documents for a staff member against a set of active shifts
   * within a date range.
   *
   * Used after full week schedule saves (DELETE-then-CREATE pattern) and after
   * batch shift deletions. Performs a single atomic Firestore batch that:
   * - Updates durationMinutes on vacations whose date exists in activeShifts (if value differs)
   * - Deletes vacations whose date has no corresponding shift in activeShifts (orphan removal)
   *
   * The lookup is built by summing totalMinutes per date from activeShifts, so multiple
   * shifts on the same day are handled correctly.
   *
   * IMPORTANT:
   * - Never creates new vacation documents
   * - Uses a single Firestore batch for atomicity
   * - Never throws to the UI. Fails silently with console.error only.
   *
   * @param {string} companyId - Company ID
   * @param {string} staffId - Staff member ID
   * @param {string} startDate - Start of date range in YYYY-MM-DD format (inclusive)
   * @param {string} endDate - End of date range in YYYY-MM-DD format (inclusive)
   * @param {Array<Object>} activeShifts - Current shifts in the range after the operation.
   *   Each object must have:
   *     - date: string (YYYY-MM-DD)
   *     - totalMinutes: number  OR  expectedHours: number (fallback)
   *   Pass an empty array to delete all vacations in the range (full week cleared).
   */
  async reconcileVacationsWithShifts(companyId, staffId, startDate, endDate, activeShifts) {
    try {
      // Build lookup map: { dateString: totalMinutes }
      // Sum per date to correctly handle multiple shifts on the same day
      const shiftMinutesMap = {};
      activeShifts.forEach(shift => {
        const date = shift.date;
        if (!date) return;

        // Support both totalMinutes (raw Firestore data) and expectedHours (ScheduleShift model)
        const minutes = typeof shift.totalMinutes === 'number'
          ? shift.totalMinutes
          : Math.round((shift.expectedHours || 0) * 60);

        shiftMinutesMap[date] = (shiftMinutesMap[date] || 0) + minutes;
      });

      // Query all vacations for this staff member within the date range
      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      const q = query(
        vacationsRef,
        where('staffId', '==', staffId),
        where('date', '>=', startDate),
        where('date', '<=', endDate)
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        console.log(`Cascade: no vacations found for staff ${staffId} between ${startDate} and ${endDate} - nothing to reconcile`);
        return;
      }

      const batch = writeBatch(db);
      let updateCount = 0;
      let deleteCount = 0;
      let unchangedCount = 0;

      snapshot.docs.forEach(vacationDoc => {
        const vacation = vacationDoc.data();
        const dateString = vacation.date;

        if (shiftMinutesMap[dateString] !== undefined) {
          // A shift exists for this date - update duration only if the value has changed
          const newMinutes = shiftMinutesMap[dateString];
          if (vacation.durationMinutes !== newMinutes) {
            batch.update(vacationDoc.ref, {
              durationMinutes: newMinutes,
              updatedAt: Timestamp.fromDate(new Date()),
              updatedBy: 'system'
            });
            updateCount++;
          } else {
            unchangedCount++;
          }
        } else {
          // No shift exists for this date - delete the orphaned vacation
          batch.delete(vacationDoc.ref);
          deleteCount++;
        }
      });

      if (updateCount > 0 || deleteCount > 0) {
        await batch.commit();
        console.log(
          `Cascade: reconciled vacations for staff ${staffId} (${startDate} to ${endDate}): ` +
          `${updateCount} updated, ${deleteCount} deleted, ${unchangedCount} unchanged`
        );
      } else {
        console.log(`Cascade: no vacation changes needed for staff ${staffId} (${startDate} to ${endDate}) - all ${unchangedCount} vacation(s) already in sync`);
      }
    } catch (error) {
      console.error(`Cascade error - reconcileVacationsWithShifts (staffId: ${staffId}, range: ${startDate} to ${endDate}):`, error);
    }
  }

  // ==================== LEGACY COMPATIBILITY ====================

  /**
   * Legacy method name for backward compatibility
   * @deprecated Use recalculateVacationMinutes instead
   */
  async recalculateVacationHours(companyId, staffId, date, departmentId) {
    console.warn('⚠️ recalculateVacationHours is deprecated, use recalculateVacationMinutes');
    return this.recalculateVacationMinutes(companyId, staffId, date, departmentId);
  }

  /**
   * Legacy method name for backward compatibility
   * @deprecated Use resetVacationMinutes instead
   */
  async resetVacationHours(companyId, staffId, date, departmentId) {
    console.warn('⚠️ resetVacationHours is deprecated, use resetVacationMinutes');
    return this.resetVacationMinutes(companyId, staffId, date, departmentId);
  }

  /**
   * Get vacation summary statistics for a month
   * @returns {VacationSummary} - Summary object with statistics
   */
  async getMonthSummary(companyId, month, year, departmentIds = null, staffMap = {}) {
    try {
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0, 23, 59, 59, 999);

      let vacations;
      if (departmentIds && departmentIds.length > 0) {
        // Get vacations for specific departments
        const allVacations = [];
        for (const deptId of departmentIds) {
          const deptVacations = await this.getVacationsByDepartment(
            companyId,
            deptId,
            startDate,
            endDate
          );
          allVacations.push(...deptVacations);
        }
        vacations = allVacations;
      } else {
        // Get all vacations for company
        vacations = await this.getVacationsByDateRange(companyId, startDate, endDate);
      }

      // Build summary (vacations are already VacationModel instances)
      const summary = new VacationSummary();
      
      // Add vacations with staff names from staffMap
      vacations.forEach(vacation => {
        const staff = staffMap[vacation.staffId];
        const staffName = staff ? staff.name : 'Unbekannter Mitarbeiter';
        summary.addVacation(vacation, staffName);
      });

      return summary;
    } catch (error) {
      console.error('Error getting month summary:', error);
      throw error;
    }
  }

  /**
   * Subscribe to vacation changes (real-time listener)
   * @param {Function} callback - Callback receives array of VacationModel instances
   * @returns {Function} - Unsubscribe function
   */
  subscribeToVacations(companyId, startDate, endDate, callback, departmentIds = null) {
    try {
      const startDateString = this._toDateString(startDate);
      const endDateString = this._toDateString(endDate);

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      
      let q;
      if (departmentIds && departmentIds.length > 0) {
        q = query(
          vacationsRef,
          where('departmentId', 'in', departmentIds),
          where('date', '>=', startDateString),
          where('date', '<=', endDateString),
          orderBy('date', 'asc')
        );
      } else {
        q = query(
          vacationsRef,
          where('date', '>=', startDateString),
          where('date', '<=', endDateString),
          orderBy('date', 'asc')
        );
      }

      return onSnapshot(q, (snapshot) => {
        // ✅ Returns array of VacationModel instances to callback
        const vacations = snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
        callback(vacations);
      }, (error) => {
        console.error('Error in vacation subscription:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error subscribing to vacations:', error);
      throw error;
    }
  }

  /**
   * Subscribe to vacations for a specific day
   * @param {Function} callback - Callback receives array of VacationModel instances
   * @returns {Function} - Unsubscribe function
   */
  subscribeToVacationsForDay(companyId, date, callback, departmentIds = null) {
    try {
      const dateString = this._toDateString(date);

      const vacationsRef = collection(db, 'companies', companyId, 'vacations');
      
      let q;
      if (departmentIds && departmentIds.length > 0) {
        q = query(
          vacationsRef,
          where('departmentId', 'in', departmentIds),
          where('date', '==', dateString)
        );
      } else {
        q = query(
          vacationsRef,
          where('date', '==', dateString)
        );
      }

      return onSnapshot(q, (snapshot) => {
        // ✅ Returns array of VacationModel instances to callback
        const vacations = snapshot.docs.map(doc => VacationModel.fromFirestore(doc));
        callback(vacations);
      }, (error) => {
        console.error('Error in vacation subscription for day:', error);
        callback([]);
      });
    } catch (error) {
      console.error('Error subscribing to vacations for day:', error);
      throw error;
    }
  }

  /**
   * Group vacations by date
   * Returns Map<dateString, VacationModel[]>
   * @param {Array<VacationModel>} vacations - Array of VacationModel instances
   * @returns {Map<string, Array<VacationModel>>}
   */
  groupVacationsByDate(vacations) {
    const grouped = new Map();
    
    vacations.forEach(vacation => {
      // ✅ Uses VacationModel's getDateString() method
      const dateStr = vacation.getDateString();
      if (!grouped.has(dateStr)) {
        grouped.set(dateStr, []);
      }
      grouped.get(dateStr).push(vacation);
    });

    return grouped;
  }

  /**
   * Group vacations by staff
   * Returns Map<staffId, VacationModel[]>
   * @param {Array<VacationModel>} vacations - Array of VacationModel instances
   * @returns {Map<string, Array<VacationModel>>}
   */
  groupVacationsByStaff(vacations) {
    const grouped = new Map();
    
    vacations.forEach(vacation => {
      if (!grouped.has(vacation.staffId)) {
        grouped.set(vacation.staffId, []);
      }
      grouped.get(vacation.staffId).push(vacation);
    });

    return grouped;
  }
}

// Export singleton instance
export default new VacationService();